import { ContractTxAsyncActionResponse } from 'common/models/crypto';
import { Nullable } from 'common/models/optional';
import { ClaimApi } from 'sdk/claimApi';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { InputUtils } from 'utils/inputUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, interpret, Machine, spawn } from 'xstate';
import { ClaimEvent } from './claimEvent';
import { XStateUtils } from './xStateUtils';
import { StorageUtils } from 'utils/storageUtils';
import {
  GeneralErrorEvent,
  GetStoredTxResponseEvent,
} from 'common/models/xstate';
import {
  createTxMachine,
  initialTxContext,
  RemoteEventExecutingTx,
  RemoteEventNoStoredTx,
  RemoteEventNoWalletConnected,
  RemoteEventTxError,
  RemoteEventTxSuccess,
  RemoteTxEvent,
} from './txMachine';
import { SwalUtils } from 'utils/swalUtils';
import { AppSyncFarm } from 'common/models/farm';
import { ChainId } from 'constants/constants';
import { Address } from 'viem';

enum ClaimState {
  BASE_STATE = 'BASE_STATE',
  CHECK_CONDITIONS = 'CHECK_CONDITIONS',
  CLAIM_AND_GET_TX = 'CLAIM_AND_GET_TX',
  LOADING_DATA = 'LOADING_DATA',
  NO_WALLET_CONNECTED = 'NO_WALLET_CONNECTED',
  CHECKING_FOR_CLAIM_TX = 'CHECKING_FOR_CLAIM_TX',
  HANDLE_CLAIM_TX = 'HANDLE_CLAIM_TX',
}

enum ClaimAction {
  CLAIM_ERROR = 'CLAIM_ERROR',
}

type ClaimSchema = {
  states: {
    [s in ClaimState]: object;
  };
};

type EventClaimArgs = {
  poolId: number;
  farm: AppSyncFarm;
};

export type EventUpdateAccountInfoArgs = {
  address: Address;
  chainId: number;
};

// NOTE that xstate always whats the value to be named 'value', don't change the name,
// The actual value can be anything, but the variable has to be called value
type EventUpdateAccountInfo = {
  type: ClaimEvent.UPDATE_ACCOUNT_INFO;
  value: EventUpdateAccountInfoArgs;
};
type EventClaim = { type: ClaimEvent.CLAIM; value: EventClaimArgs };

type ClaimEvents =
  | EventClaim
  | EventUpdateAccountInfo
  // Remote Tx Events
  | RemoteEventExecutingTx
  | RemoteEventTxError
  | RemoteEventTxSuccess
  | RemoteEventNoWalletConnected
  | RemoteEventNoStoredTx;

type ClaimContext = {
  inputError?: string;
  accountAddress: Nullable<Address>;
  chainId: Nullable<number>;
  ichiBalance: bigint;
  poolId: number;
  gasLimit: bigint;
  farm: Nullable<AppSyncFarm>;
  isClaiming: boolean;
  claimResponse: Nullable<ContractTxAsyncActionResponse>;
  claimError: Nullable<Error>;
  claimDataResponse: Nullable<ClaimApi.GetClaimDataResponse>;
  storedTx: Nullable<StorageUtils.StoredTx>;
  claimDataError: Nullable<Error>;
  isLoadingData: boolean;
  txRef: any;
};

export const initialClaimContext: ClaimContext = {
  inputError: undefined,
  accountAddress: null,
  chainId: null,
  ichiBalance: 0n,
  poolId: 0,
  gasLimit: CryptoUtils.calculateGasMargin(
    BigInt(GAS_SETTINGS.claim),
    ChainId.Mainnet
  ),
  farm: null,
  isClaiming: false,
  isLoadingData: false,
  claimResponse: null,
  claimError: null,
  claimDataResponse: null,
  storedTx: null,
  claimDataError: null,
  txRef: null,
};

type GetClaimDataResponseEvent = {
  type: string;
  data: ClaimApi.GetClaimDataResponse;
};

const claimApi = ClaimApi.Claim.getInstance();

const claimMachine = Machine<ClaimContext, ClaimSchema, ClaimEvents>(
  {
    key: 'claim',
    initial: ClaimState.BASE_STATE,
    context: XStateUtils.shortStringify(initialClaimContext),
    states: {
      [ClaimState.BASE_STATE]: {
        on: {
          // Once the user has a connected wallet then let's check for existing transactions
          [ClaimEvent.UPDATE_ACCOUNT_INFO]: {
            target: ClaimState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
            }),
          },
          [ClaimEvent.CLAIM]: {
            target: ClaimState.CLAIM_AND_GET_TX,
            actions: assign({
              poolId: (_, event: EventClaim) => event.value.poolId,
              farm: (_, event: EventClaim) => event.value.farm,
              isClaiming: () => true,
              claimResponse: () => null,
            }),
          },
        },
      },
      // NOTE here I'm really assuming the only possible tx is staking, so if we want to support more types
      // in the future, would need to rework this some.
      [ClaimState.CHECKING_FOR_CLAIM_TX]: {
        invoke: {
          src: (context) => {
            return TransactionUtils.getStoredTx({
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              functionName: ClaimApi.Claim.claimFN,
            });
          },
          // Success means there is a mint tx, re-execute mint
          onDone: {
            target: ClaimState.CLAIM_AND_GET_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          // Error just means no tx found, go back to waiting for input
          onError: {
            target: ClaimState.CHECK_CONDITIONS,
          },
        },
      },
      [ClaimState.CLAIM_AND_GET_TX]: {
        entry: assign({
          isClaiming: () => true,
        }),
        exit: assign({
          isClaiming: () => false,
        }),
        invoke: {
          src: (context) => {
            return claimApi.claim({
              chainId: context.chainId as number,
              accountAddress: context.accountAddress as Address,
              poolId: context.poolId,
              farm: context.farm as AppSyncFarm,
            });
          },
          onDone: {
            // Here we aren't going to indicate success as this is 'detached' async response where
            // we have to wait for the actual tx to finish, which is being tracked in localstorage/indexDB
            // See the activities below
            target: ClaimState.HANDLE_CLAIM_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: ClaimState.BASE_STATE,
            actions: [
              ClaimAction.CLAIM_ERROR,
              assign({
                claimError: (_, event: GeneralErrorEvent) => event.data,
                isClaiming: () => false,
              }),
            ],
          },
        },
      },
      [ClaimState.HANDLE_CLAIM_TX]: {
        entry: assign({
          isClaiming: () => true,
          txRef: (context) => {
            const c = context as unknown as ClaimContext;
            return spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: c.chainId,
                accountAddress: c.accountAddress,
                storedTx: c.storedTx,
              }),
              { sync: true, name: `tx-machine-${c.storedTx?.hash}` }
            );
          },
        }),
        exit: assign({
          isClaiming: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: ClaimState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: ClaimState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: ClaimState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: RemoteTxEvent.REMOTE_TX_SUCCESS,
            target: ClaimState.LOADING_DATA,
          },
        },
      },
      [ClaimState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
        }),
        exit: assign({
          isLoadingData: () => false,
        }),
        invoke: {
          src: (context) => {
            return claimApi.getClaimData({
              accountAddress: context.accountAddress as Address,
              chainId: context.chainId as number,
              farm: context.farm as AppSyncFarm,
              poolId: context.poolId,
            });
          },
          onDone: {
            target: ClaimState.CHECKING_FOR_CLAIM_TX,
            actions: assign({
              claimDataResponse: (_, event: GetClaimDataResponseEvent) =>
                event.data,
              ichiBalance: (_, event: GetClaimDataResponseEvent) =>
                event.data.ichiBalance,
            }),
          },
          onError: {
            target: ClaimState.CHECK_CONDITIONS,
            actions: assign({
              claimDataError: (_, event: GeneralErrorEvent) => event.data,
            }),
          },
        },
      },
      [ClaimState.CHECK_CONDITIONS]: {
        always: [
          {
            target: ClaimState.NO_WALLET_CONNECTED,
            cond: InputUtils.isNullChainId,
          },
          {
            target: ClaimState.BASE_STATE,
            cond: InputUtils.alwaysTrue,
          },
        ],
      },
      [ClaimState.NO_WALLET_CONNECTED]: {
        entry: assign({
          inputError: () => 'No wallet Connected!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [ClaimEvent.CLAIM]: {
            target: ClaimState.LOADING_DATA,
            actions: assign({
              isClaiming: () => true,
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      [ClaimAction.CLAIM_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Claiming!',
        });
      },
    },
  }
);

export const claimService = interpret(claimMachine, {
  // devTools: process.env.NODE_ENV === 'development'
}).start();

claimService.subscribe((s) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[claimService:debug]', s);
  }
});
